// Regra de fila do discador — GLOBAL por org (feature `queue-config` do back).
// Como os leads são ORDENADOS e RETENTADOS na fila: peso/ordem por vendedor e
// teto+cadência por desfecho técnico da tentativa. É configuração da
// ORGANIZAÇÃO, não atributo de campanha — daí o módulo próprio, separado de
// ./dialer.js: nenhum dos dois importa o outro, e a chave de cache
// (["queue-config"]) também é uma raiz só dele. A campanha SOBRESCREVE parte
// disto em `retryPolicy` (ver ./dialer.js), mas a herança acontece no back.
// GET é aberto a qualquer membro da org; o PATCH é de gestor (403 no back).
import { api, toApiError } from "./client.js";

// DESFECHO TÉCNICO da tentativa — a chave da cadência por desfecho. Vem de
// call.status + call.failureReason (o que a telefonia observou), não da
// tabulação que o vendedor escolhe. Espelha
// back/src/features/queue-config/attempt-outcome.ts: um desfecho novo lá precisa
// de entrada aqui. A ordem é a ordem da tela.
export const ATTEMPT_OUTCOMES = ["no_answer", "busy", "voicemail", "answered", "carrier"] as const;
export type AttemptOutcome = (typeof ATTEMPT_OUTCOMES)[number];

export const ATTEMPT_OUTCOME_LABELS: Record<AttemptOutcome, string> = {
  no_answer: "Não atendeu",
  busy: "Ocupado",
  voicemail: "Caixa postal",
  answered: "Atendeu e não fechou",
  carrier: "Falha na operadora",
};

// Por que cada desfecho merece um ritmo diferente — o gestor decide melhor
// sabendo o que a telefonia viu.
export const ATTEMPT_OUTCOME_HINTS: Record<AttemptOutcome, string> = {
  no_answer: "Chamou e ninguém atendeu.",
  busy: "O lead estava ao telefone: existe, atende, e desocupa logo.",
  voicemail: "Quem atendeu foi a caixa postal. Insistir de perto queima o número.",
  answered: "Falou com alguém e a ligação não fechou o assunto.",
  carrier: "Operadora congestionada ou falha ao originar. O lead nem soube da ligação.",
};

export interface AttemptRule {
  // Switch do desfecho: `false` desliga (o lead PARA neste desfecho, não volta
  // para a fila). Ausente vale ligado — as regras salvas antes do switch não têm
  // o campo. Teto e intervalos continuam gravados quando desligado, para religar
  // devolver a regra inteira.
  enabled?: boolean;
  maxAttempts: number;
  // Espera por posição DENTRO do desfecho: [após a 1ª, após a 2ª…], em minutos.
  cadenceMinutes: number[];
}

// Desfecho ausente herda maxAttempts/cadenceMinutes globais.
export type AttemptRules = Partial<Record<AttemptOutcome, AttemptRule>>;

export interface QueueConfig {
  id: string;
  organizationId: string;
  sellerWeights: Record<string, number>;
  sellerOrder: string[];
  retryDelays: Record<string, number>;
  retryPenalty: number;
  maxAttempts: number;
  // DEFAULT da regra de retry das campanhas (D2). A campanha herda daqui quando
  // não sobrescreve em campaign.retryPolicy.
  cadenceMinutes: number[];
  maxAttemptsPerDay: number | null;
  // Teto e espera POR DESFECHO técnico, cada um com contador próprio. É o que a
  // tela de regra de fila edita; maxAttempts/cadenceMinutes acima só valem para
  // desfecho sem regra.
  attemptRules: AttemptRules;
  // Só entra na fila do vendedor quem tem `etapa_discador`. Sem ela o lead herda
  // o status default da org (o de maior peso) e fura a fila. Liga por marca.
  requireDialerStage: boolean;
  createdAt: string;
  updatedAt: string;
}

export async function getQueueConfig(): Promise<QueueConfig> {
  const { data, error } = await api<QueueConfig>("/v1/queue-config");
  if (error) throw toApiError("GET /v1/queue-config", error);
  return data;
}

export async function updateQueueConfig(
  patch: Partial<{
    sellerWeights: Record<string, number>;
    sellerOrder: string[];
    retryDelays: Record<string, number>;
    retryPenalty: number;
    maxAttempts: number;
    cadenceMinutes: number[];
    maxAttemptsPerDay: number | null;
    // SUBSTITUI o mapa inteiro no back (não faz merge).
    attemptRules: AttemptRules;
    requireDialerStage: boolean;
  }>,
): Promise<QueueConfig> {
  const { data, error } = await api<QueueConfig>("/v1/queue-config", {
    method: "PATCH",
    body: patch,
  });
  if (error) throw toApiError("PATCH /v1/queue-config", error);
  return data;
}
